import React, { useState, useEffect } from 'react';
import theme from '../../theme/theme';
import { Api } from '../../network/api';
import { PaperIcon } from '../../assets/icons';

const { Colors } = theme;

const TransactionsHistory = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await Api.getMyTransactions(20);
      if (response?.data && response?.data?.transactions && response?.data?.transactions?.length > 0) {
        setTransactions(response?.data?.transactions);
      } else {
        // Handle case when API returns 200 with no data or empty array
        // Check for message in response
        const message = response?.data?.message || response?.data?.detail || 'No billing history found for this user';
        setError(message);
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      if (err.response?.status === 404) {
        const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'No billing history found for this user';
        setError(errorMessage);
        setTransactions([]);
      } else {
        setError('Failed to load transactions. Please try again.');
        setTransactions([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount, currency = 'usd') => {
    if (!amount) return '$0.00';
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    });
    return formatter.format(amount / 100);
  };

  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      case 'pending':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'failed':
      case 'void':
        return { backgroundColor: '#fee2e2', color: '#991b1b' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#374151' };
    }
  };

  const handleLinkClick = (url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <main className="">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Transaction History</h1>
            <p className="text-gray-600 mt-1">View all your billing transactions and invoices</p>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                <span className="ml-2 text-gray-600">Loading transactions...</span>
              </div>
            ) : error && error.includes('No billing history') ? (
              <div className="text-center py-12">
                <PaperIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No billing history found</h3>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading transactions</h3>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
                <button
                  onClick={fetchTransactions}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 hover:opacity-90"
                  style={{
                    backgroundImage: `linear-gradient(to right, ${Colors.primary}, ${Colors.secondary})`,
                  }}
                >
                  Retry
                </button>
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-12">
                <PaperIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No transactions found</h3>
                <p className="mt-1 text-sm text-gray-500">Your transaction history will appear here.</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr
                      key={transaction.id}
                      className="hover:bg-gray-50 transition-colors duration-200"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.created)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {formatCurrency(transaction.amount_paid, transaction.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(transaction.period_end)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          style={getStatusStyle(transaction.status)}
                        >
                          {transaction.status || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm flex justify-center">
                        <div className="flex md:flex-row flex-col gap-2 md:gap-9">
                          {transaction.hosted_invoice_url && (
                            <button
                              onClick={() => handleLinkClick(transaction.hosted_invoice_url)}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              See Invoice
                            </button>
                          )}
                          {transaction.invoice_pdf && (
                            <button
                              onClick={() => handleLinkClick(transaction.invoice_pdf)}
                              className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                            >
                              Invoice PDF
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default TransactionsHistory;

